// src/screens/NuestrosClientesPublicScreen.jsx
// Réplica pública (sin login) de "Nuestros Clientes", servida en
// app.clicktravelagency.com.br/nuestros-clientes. Se puede ver y también dejar
// una reseña sin cuenta (nombre + teléfono, con fotos opcionales); toda reseña queda
// pendiente hasta que el admin la apruebe.
import React, { useEffect, useState } from 'react';
import NuestrosClientesView from '../components/NuestrosClientesView';
import { enviarResenaPublica, getGoogleReviewLinkPublico } from '../services/nuestrosClientesService';
import { notifyAdmin } from '../services/authService';

// La mayoría de quien recibe este link (por WhatsApp) no tiene página
// anterior real a la que volver -- si le da "atrás" del navegador,
// mejor mandarlo a /vuelos-guyana (pública, sin login, sigue siendo
// relevante) que dejarlo en una pantalla en blanco o fuera de la app.
const DESTINO_AL_VOLVER = '/vuelos-guyana';

function useAtraparBotonAtras(destino) {
  useEffect(() => {
    window.history.pushState({ ncBack: true }, '', window.location.href);
    const onPopState = () => { window.location.href = destino; };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [destino]);
}

const inputStyle = {
  width: '100%', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12,
  padding: 12, color: 'var(--white)', fontSize: 13.5, marginBottom: 16, boxSizing: 'border-box',
};

// Mensajes de la base de datos que sí se le pueden mostrar tal cual a la persona.
const ERRORES_CLAROS = ['Escribe tu nombre', 'Elige una cantidad', 'Escribe un teléfono', 'El comentario es demasiado largo'];

const MAX_FOTOS = 3;

// Achica la foto (lado mayor 1600px, JPEG) antes de subirla: las fotos del celular pesan varios MB.
// Si algo falla (formato raro), se sube la original.
function achicarFoto(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const escala = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * escala);
      canvas.height = Math.round(img.height * escala);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob ? new File([blob], 'foto.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

function FormResenaPublica({ onClose }) {
  const [nombre, setNombre] = useState('');
  const [rating, setRating] = useState(0);
  const [comentario, setComentario] = useState('');
  const [telefono, setTelefono] = useState('');
  const [fotos, setFotos] = useState([]); // File[]
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [enviada, setEnviada] = useState(false);
  const [googleLink, setGoogleLink] = useState(null);

  useEffect(() => { getGoogleReviewLinkPublico().then(setGoogleLink); }, []);

  const previews = React.useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const agregarFotos = async (files) => {
    const nuevas = await Promise.all(Array.from(files || []).map(achicarFoto));
    setFotos((prev) => [...prev, ...nuevas].slice(0, MAX_FOTOS));
  };
  const quitarFoto = (i) => setFotos((prev) => prev.filter((_, idx) => idx !== i));

  const enviar = async () => {
    setError('');
    if (nombre.trim().length < 2) return setError('Escribe tu nombre.');
    if (!rating) return setError('Elige una cantidad de estrellas.');
    if (telefono.replace(/\D/g, '').length < 8) return setError('Escribe tu teléfono (WhatsApp).');
    setEnviando(true);
    try {
      await enviarResenaPublica({ nombre, rating, comentario, telefono, fotos });
      notifyAdmin(`⭐ Nueva reseña de ${nombre.trim()} (${rating}★) — pendiente de moderar`, '/?tab=more&admin=nuestros_clientes&sub=resenas');
      setEnviada(true);
    } catch (err) {
      const msg = err?.message || '';
      setError(ERRORES_CLAROS.some((m) => msg.includes(m)) ? msg : 'No se pudo enviar tu reseña. Intenta de nuevo.');
    }
    setEnviando(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 300, overflowY: 'auto' }}>
      <div style={{ padding: '24px 16px 40px', maxWidth: 480, margin: '0 auto' }}>
        {enviada ? (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <span style={{ fontSize: 44 }}>🙏</span>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '10px 0 6px' }}>¡Gracias por tu opinión!</h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 22 }}>La recibimos y está en revisión.</p>
            {rating >= 4 && googleLink && (
              <a href={googleLink} target="_blank" rel="noopener noreferrer" style={{
                display: 'block', background: 'var(--orange)', color: 'white', fontWeight: 800, fontSize: 14.5,
                borderRadius: 14, padding: '16px 0', textDecoration: 'none', marginBottom: 16,
              }}>Si te gustó, cuéntalo también en Google 🙌</a>
            )}
            <button onClick={onClose} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 12, padding: '11px 20px', fontSize: 13 }}>Volver</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)' }}>Deja tu reseña</h2>
              <button onClick={onClose} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--muted)', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>✕</button>
            </div>

            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Tu nombre (se publica junto a tu opinión)</p>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={60} placeholder="Nombre" style={inputStyle} />

            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>¿Cómo calificarías tu experiencia?</p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} style={{ background: 'transparent', padding: 0, fontSize: 30, color: n <= rating ? 'var(--gold, #FFB800)' : 'var(--navy-border)' }}>★</button>
              ))}
            </div>

            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Tu opinión (opcional)</p>
            <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={5} maxLength={1000} placeholder="Cuéntanos cómo fue tu experiencia..." style={{ ...inputStyle, resize: 'vertical' }} />

            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Tu teléfono / WhatsApp (obligatorio, para verificar que fuiste cliente — no se publica)</p>
            <input value={telefono} onChange={(e) => setTelefono(e.target.value)} inputMode="tel" placeholder="WhatsApp" style={inputStyle} />

            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Fotos (opcional, hasta {MAX_FOTOS})</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {previews.map((u, i) => (
                <div key={u} style={{ position: 'relative', width: 72, height: 72 }}>
                  <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
                  <button onClick={() => quitarFoto(i)} style={{
                    position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11,
                    background: '#FF4455', color: 'white', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                </div>
              ))}
              {fotos.length < MAX_FOTOS && (
                <label style={{
                  width: 72, height: 72, borderRadius: 10, border: '1px dashed var(--navy-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 24, cursor: 'pointer',
                }}>
                  +
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => { agregarFotos(e.target.files); e.target.value = ''; }} />
                </label>
              )}
            </div>

            {error && <p style={{ fontSize: 12.5, color: 'var(--error, #FF4455)', fontWeight: 700, marginBottom: 12 }}>{error}</p>}
            <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>Tu reseña se revisa antes de publicarse.</p>

            <button onClick={enviar} disabled={enviando} style={{
              width: '100%', background: 'var(--orange)', color: 'white', fontWeight: 800, fontSize: 14,
              borderRadius: 14, padding: '15px 0',
            }}>{enviando ? 'Enviando...' : 'Enviar reseña'}</button>
          </>
        )}
      </div>
    </div>
  );
}

function CTAResena({ onClick }) {
  return (
    <div style={{ margin: '14px 16px 0', textAlign: 'center' }}>
      <button onClick={onClick} style={{
        display: 'inline-block', background: 'var(--orange)', color: 'white', fontWeight: 800, fontSize: 13,
        borderRadius: 20, padding: '11px 22px', border: 'none', cursor: 'pointer',
      }}>⭐ Dejar mi reseña</button>
    </div>
  );
}

function Header({ destino }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'var(--bg)', borderBottom: '1px solid var(--navy-border)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <img src="/logo-principal.png" alt="Click Travel Agency" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover' }} />
      <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)', flex: 1 }}>Click Travel Agency</span>
      <a href={destino} style={{
        fontSize: 12.5, fontWeight: 700, color: 'var(--orange)', textDecoration: 'none',
        border: '1px solid var(--navy-border)', borderRadius: 10, padding: '7px 12px',
      }}>Ver vuelos →</a>
    </div>
  );
}

export default function NuestrosClientesPublicScreen({ highlightShareToken }) {
  useAtraparBotonAtras(DESTINO_AL_VOLVER);
  const [formAbierto, setFormAbierto] = useState(false);
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Header destino={DESTINO_AL_VOLVER} />
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <CTAResena onClick={() => setFormAbierto(true)} />
        <NuestrosClientesView canReview={false} highlightShareToken={highlightShareToken} />
      </div>
      {formAbierto && <FormResenaPublica onClose={() => setFormAbierto(false)} />}
    </div>
  );
}
